// @ts-check
// Cache de registros por consulta viva. Atualizações otimistas aplicam o patch em todas as
// listas abertas do mesmo objeto e desfazem em caso de erro (como o cache do Apollo no Twenty).
import { signal } from '@preact/signals';
import { createOneRecord, deleteOneRecord, updateOneRecord } from './rest.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../ui/snackbar.js';
import { t, translateMetadataLabel } from '../i18n/index.js';
import { isRecordOutsideStageScope } from './stage-scope.js';
import { markRecordHandedOff } from '../lib/handed-off-records.js';

/**
 * @typedef {import('../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {{ objectNameSingular: string, records: import('@preact/signals').Signal<any[]>, isTrash?: () => boolean }} LiveRecordList
 */

/** @type {Set<LiveRecordList>} */
const liveRecordLists = new Set();

/** Incrementado a cada escrita confirmada; consultas agregadas (kanban, contagens) o observam. */
export const mutationCounterState = signal(0);

/**
 * Registros novos (ou restaurados) que outra pessoa criou num objeto: as listas abertas dele recarregam a primeira página
 * em silêncio quando faz sentido (contrato 08 de navegação, seção 6). Chave: objectNameSingular; valor: contador.
 * @type {import('@preact/signals').Signal<Record<string, number>>}
 */
export const remoteListChangeState = signal({});

/**
 * @param {string} objectNameSingular
 * @param {any[]} [initialRecords]
 * @param {{ isTrash?: boolean | (() => boolean) }} [options] lista da lixeira (ou que mostra excluídos): registros excluídos por
 *   outra pessoa não somem dela; função quando a lista entra e sai do modo lixeira
 * @returns {LiveRecordList & { dispose: () => void }}
 */
export function createLiveRecordList(objectNameSingular, initialRecords = [], options = {}) {
  const isTrash = options.isTrash;
  const list = { objectNameSingular, records: signal(initialRecords), isTrash: typeof isTrash === 'function' ? isTrash : () => Boolean(isTrash) };
  liveRecordLists.add(list);
  return { ...list, dispose: () => liveRecordLists.delete(list) };
}

/**
 * Aplica um patch local e devolve a função que o desfaz.
 * @param {string} objectNameSingular
 * @param {string} recordId
 * @param {Record<string, unknown>} localPatch
 */
export function patchRecordLocally(objectNameSingular, recordId, localPatch) {
  /** @type {{ list: LiveRecordList, previous: any[] }[]} */
  const snapshots = [];
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    const previous = list.records.value;
    if (!previous.some((record) => record.id === recordId)) continue;
    snapshots.push({ list, previous });
    list.records.value = previous.map((record) => (record.id === recordId ? { ...record, ...localPatch } : record));
  }
  return () => {
    for (const { list, previous } of snapshots) list.records.value = previous;
  };
}

/**
 * @param {ObjectMetadata} objectMetadata
 * @param {string} recordId
 * @param {Record<string, unknown>} apiPatch corpo do PATCH (relações como <campo>Id)
 * @param {Record<string, unknown>} [localPatch] patch para exibição (pode incluir o objeto da relação)
 */
export async function updateRecordOptimistically(objectMetadata, recordId, apiPatch, localPatch = apiPatch) {
  const revert = patchRecordLocally(objectMetadata.nameSingular, recordId, localPatch);
  try {
    const updatedRecord = await updateOneRecord(objectMetadata, recordId, apiPatch);
    mutationCounterState.value += 1;
    // card que foi para uma etapa que o papel não vê (Pode passar, ou regra do motor): sai das listas na hora, sem
    // esperar o feed, que ignora as próprias mudanças (contrato de equipe, seção 3.2)
    const finalRecord = { ...localPatch, ...(updatedRecord ?? {}) };
    if (isRecordOutsideStageScope(objectMetadata.nameSingular, finalRecord)) {
      const option = objectMetadata.fields.find((field) => field.name === 'stage')?.options?.find((candidate) => candidate.value === finalRecord.stage);
      const stageLabel = translateMetadataLabel(option?.label ?? String(finalRecord.stage));
      // a página e o painel abertos no card mostram o repasse, não o 404 (marcado antes de o registro sair das listas)
      markRecordHandedOff(objectMetadata.nameSingular, recordId, stageLabel);
      removeRecordsLocally(objectMetadata.nameSingular, [recordId]);
      showSuccessSnackbar(t('stageTransition.handoffDone', { stage: stageLabel }));
    }
    return updatedRecord;
  } catch (error) {
    revert();
    showErrorSnackbar(error);
    throw error;
  }
}

/**
 * @param {string} objectNameSingular
 * @param {any} record
 * @param {{ atStart?: boolean }} [options]
 */
export function insertRecordLocally(objectNameSingular, record, { atStart = false } = {}) {
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    if (list.records.value.some((existing) => existing.id === record.id)) continue;
    list.records.value = atStart ? [record, ...list.records.value] : [...list.records.value, record];
  }
}

/**
 * @param {ObjectMetadata} objectMetadata
 * @param {Record<string, unknown>} data
 */
export async function createRecord(objectMetadata, data) {
  const record = await createOneRecord(objectMetadata, data);
  mutationCounterState.value += 1;
  return record;
}

/**
 * @param {ObjectMetadata} objectMetadata
 * @param {string} recordId
 */
export async function deleteRecordOptimistically(objectMetadata, recordId) {
  /** @type {{ list: LiveRecordList, previous: any[] }[]} */
  const snapshots = [];
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectMetadata.nameSingular) continue;
    snapshots.push({ list, previous: list.records.value });
    list.records.value = list.records.value.filter((record) => record.id !== recordId);
  }
  try {
    await deleteOneRecord(objectMetadata, recordId);
    mutationCounterState.value += 1;
  } catch (error) {
    for (const { list, previous } of snapshots) list.records.value = previous;
    showErrorSnackbar(error);
    throw error;
  }
}

/** Ids de registros do objeto carregados em alguma lista aberta. @param {string} objectNameSingular */
export function readLoadedRecordIds(objectNameSingular) {
  const ids = new Set();
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    for (const record of list.records.value) ids.add(record.id);
  }
  return ids;
}

/**
 * Registro como está agora em alguma lista aberta, ou undefined (o editor do CEP confere se o CEP ainda é o consultado).
 * @param {string} objectNameSingular @param {string} recordId
 */
export function readLoadedRecord(objectNameSingular, recordId) {
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    const found = list.records.value.find((record) => record.id === recordId);
    if (found) return found;
  }
  return undefined;
}

/**
 * Mudanças de outra pessoa (feed de mudanças): registros recarregados entram no lugar dos antigos; excluídos saem das
 * listas que não são da lixeira.
 * @param {string} objectNameSingular @param {any[]} freshRecords @param {string[]} removedIds
 */
export function applyRemoteRecords(objectNameSingular, freshRecords, removedIds) {
  const freshById = new Map(freshRecords.map((record) => [record.id, record]));
  const removed = new Set(removedIds);
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    const current = list.records.value;
    if (!current.some((record) => freshById.has(record.id) || removed.has(record.id))) continue;
    list.records.value = current
      .filter((record) => list.isTrash?.() || !removed.has(record.id))
      .map((record) => (freshById.has(record.id) ? { ...record, ...freshById.get(record.id) } : record));
  }
}

/**
 * Tira registros de todas as listas abertas, inclusive as da lixeira: o registro saiu do escopo de etapas do papel e o
 * servidor não o devolve mais em nenhuma leitura.
 * @param {string} objectNameSingular @param {string[]} recordIds
 */
export function removeRecordsLocally(objectNameSingular, recordIds) {
  const removed = new Set(recordIds);
  if (removed.size === 0) return;
  for (const list of liveRecordLists) {
    if (list.objectNameSingular !== objectNameSingular) continue;
    if (!list.records.value.some((record) => removed.has(record.id))) continue;
    list.records.value = list.records.value.filter((record) => !removed.has(record.id));
  }
}
